import { useApp, useUserSettings } from "@next-platform/client-common-core";
import { RECOMBEE_REQUEST_TIMEOUT_LOCAL_STORAGE } from "@next-platform/client-common-core/infra/constants";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { useEffect, useState } from "react";

import {
  DEFAULT_HOMEPAGE_RECOMMENDATIONS_NUM,
  HOMEPAGE_RECOMMENDATIONS_LOCAL_STORAGE_KEY_PREFIX,
  NSFW_LOCAL_STORAGE_KEY,
  SFW_LOCAL_STORAGE_KEY,
} from "@/constants";
import { useRecombee } from "@/hooks";
import { IRecommItems } from "@/hooks/types";

interface UseCharacterRecommendationsProps {
  shouldInject: boolean;
  isAuthLoading: boolean;
  userId?: string;
  nsfwToggle: boolean;
}

export const useCharacterRecommendations = ({ shouldInject, isAuthLoading, userId, nsfwToggle }: UseCharacterRecommendationsProps) => {
  const [recommendations, setRecommendations] = useState<IRecommItems | null>(null);
  const { getCachedRecommendations } = useRecombee();
  const { blockedUsers, selectedTags: selectedTagsStore } = useUserSettings();
  const { recombeeRecommendationsConfig } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const { homepageScenarioName: scenario, homepageRecommendationsFetchCount } = recombeeRecommendationsConfig ?? {};
  const homepageRecommCount = homepageRecommendationsFetchCount ?? DEFAULT_HOMEPAGE_RECOMMENDATIONS_NUM;

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!shouldInject || isAuthLoading || !userId || !scenario) {
        return;
      }

      const nsfwKey = nsfwToggle ? NSFW_LOCAL_STORAGE_KEY : SFW_LOCAL_STORAGE_KEY;

      const fetchedRecommendations = await getCachedRecommendations({
        storageKey: `${HOMEPAGE_RECOMMENDATIONS_LOCAL_STORAGE_KEY_PREFIX}_${nsfwKey}`,
        timeOut: Number(RECOMBEE_REQUEST_TIMEOUT_LOCAL_STORAGE),
        userId,
        scenario,
        recommendationsCount: homepageRecommCount,
        blockedUsers,
        selectedTags: selectedTagsStore,
        nsfwToggle,
        applicationId,
      });

      setRecommendations(fetchedRecommendations);
    };

    fetchRecommendations();
  }, [
    shouldInject,
    isAuthLoading,
    userId,
    nsfwToggle,
    scenario,
    homepageRecommCount,
    blockedUsers,
    selectedTagsStore,
    applicationId,
    getCachedRecommendations,
  ]);

  return recommendations;
};
